import React, {useMemo} from "react";
import Chart from "react-apexcharts";
import {Col} from "react-bootstrap";
import {NpsData} from "../../../types/admin";
import {ADMIN_LANG} from "../../../settings";

type Props = {
    npsScale: NpsData | undefined;
    dateStart?: string;
    dateEnd?: string;
}

const getMonthDateFromKey = (monthKey: string): Date | null => {
    const [year, month] = monthKey.split('-').map(Number);
    if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
        return null;
    }
    return new Date(Date.UTC(year, month - 1, 1));
};

const getMonthKey = (date: Date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

const getMonthLabel = (monthKey: string) => {
    const date = getMonthDateFromKey(monthKey);
    if (!date) {
        return monthKey;
    }
    return `${String(date.getUTCMonth() + 1).padStart(2, '0')}/${date.getUTCFullYear()}`;
};

const parseDate = (value?: string) => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const isDateOnlyValue = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

const buildMonthKeysFromRange = (startValue?: string, endValue?: string) => {
    const startDate = parseDate(startValue);
    const parsedEndDate = parseDate(endValue);

    if (!startDate || !parsedEndDate || !endValue) {
        return null;
    }

    const endDate = new Date(parsedEndDate.getTime());
    // API accepts date-only `date_end` as an inclusive day; convert to next-day exclusive.
    if (isDateOnlyValue(endValue)) {
        endDate.setUTCDate(endDate.getUTCDate() + 1);
    }

    const monthKeys: string[] = [];
    const cursor = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1));
    // Include only months whose first day is before the effective exclusive end.
    while (cursor < endDate) {
        monthKeys.push(getMonthKey(cursor));
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }

    return monthKeys;
};

const getNpsScore = (promoters: number, detractors: number, total: number) => {
    if (total <= 0) {
        return null;
    }
    return Math.round((((promoters - detractors) / total) * 100) * 10) / 10;
};

const NumberScaleNpsTrendChart = ({npsScale, dateStart, dateEnd}: Props) => {
    const noDataText = (ADMIN_LANG as {[key: string]: string}).no_data || 'No data';
    const monthlySeriesLabel = (ADMIN_LANG as {[key: string]: string}).nps_monthly_average || 'Monthly average NPS';
    const cumulativeSeriesLabel = (ADMIN_LANG as {[key: string]: string}).nps_cumulative_average || 'Cumulative average NPS';
    const totalLabel = (ADMIN_LANG as {[key: string]: string}).total || 'Total';

    const monthlyChartData = useMemo(() => {
        if (!npsScale) {
            return {
                categories: [] as string[],
                monthlyValues: [] as (number | null)[],
                cumulativeValues: [] as (number | null)[],
                responsesByMonth: [] as number[],
            };
        }

        const buckets = npsScale.monthly_buckets || [];
        const bucketByMonth = new Map(buckets.map(bucket => [bucket.month, bucket]));
        const rangeMonthKeys = buildMonthKeysFromRange(dateStart, dateEnd);
        const monthKeys = rangeMonthKeys
            ? rangeMonthKeys
            : Array.from(bucketByMonth.keys()).sort((a, b) => a.localeCompare(b));

        let cumulativePromoters = 0;
        let cumulativeDetractors = 0;
        let cumulativeTotal = 0;

        const categories: string[] = [];
        const monthlyValues: (number | null)[] = [];
        const cumulativeValues: (number | null)[] = [];
        const responsesByMonth: number[] = [];

        monthKeys.forEach(monthKey => {
            const bucket = bucketByMonth.get(monthKey);
            const monthResponses = bucket?.total || 0;

            categories.push(getMonthLabel(monthKey));
            responsesByMonth.push(monthResponses);

            if (bucket && monthResponses > 0) {
                cumulativePromoters += bucket.promoters;
                cumulativeDetractors += bucket.detractors;
                cumulativeTotal += monthResponses;

                monthlyValues.push(bucket.average_nps);
                cumulativeValues.push(getNpsScore(cumulativePromoters, cumulativeDetractors, cumulativeTotal));
                return;
            }

            monthlyValues.push(null);
            cumulativeValues.push(getNpsScore(cumulativePromoters, cumulativeDetractors, cumulativeTotal));
        });

        return {
            categories,
            monthlyValues,
            cumulativeValues,
            responsesByMonth
        };
    }, [npsScale, dateStart, dateEnd]);

    const npsSeries = useMemo(() => ([
        {
            name: monthlySeriesLabel,
            data: monthlyChartData.monthlyValues,
        },
        {
            name: cumulativeSeriesLabel,
            data: monthlyChartData.cumulativeValues,
        },
    ]), [monthlyChartData, cumulativeSeriesLabel, monthlySeriesLabel]);

    const npsLineOptions: ApexCharts.ApexOptions = useMemo(() => ({
        chart: {
            type: 'line',
            toolbar: {
                show: false,
            },
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            curve: 'smooth',
            width: 3,
        },
        legend: {
            show: true,
            position: 'top',
        },
        xaxis: {
            categories: monthlyChartData.categories,
            title: {
                text: ADMIN_LANG.month,
            },
            labels: {
                rotate: -45,
                rotateAlways: true,
                hideOverlappingLabels: false,
                trim: true,
            },
        },
        yaxis: {
            min: -100,
            max: 100,
            tickAmount: 4,
            title: {
                text: ADMIN_LANG.nps_rating,
            },
            labels: {
                formatter: (val: number) => val.toFixed(0),
            },
        },
        markers: {
            size: 4,
        },
        tooltip: {
            shared: true,
            intersect: false,
            y: {
                formatter: (val: number | null, {dataPointIndex}: { dataPointIndex: number }) => {
                    if (val === null || Number.isNaN(Number(val))) {
                        return noDataText;
                    }

                    const responses = monthlyChartData.responsesByMonth[dataPointIndex] || 0;
                    return `${Number(val).toFixed(1)} (${totalLabel}: ${responses})`;
                },
            },
        },
    }), [monthlyChartData, noDataText, totalLabel]);

    if (!npsScale) {
        return null;
    }

    return (
        <Col xs={12}>
            <div className='admin-card'>
                {monthlyChartData.categories.length > 0
                    ? <Chart options={npsLineOptions} series={npsSeries} type='line' height={350}/>
                    : <h3>{noDataText}</h3>
                }
            </div>
        </Col>
    );
};

export default NumberScaleNpsTrendChart;
